import React, { useContext, useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import ReactTags, { Tag } from 'react-tag-autocomplete';
import Card from 'src/components/common/Card.redesign';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { TagComponent } from 'src/components/controls/forms/redesign/TagComponents';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import DialogList from 'src/dialogs';
import County from 'src/models/County';
import { convertInstrumentTypesToSelectList } from 'src/models/InstrumentType';
import { LandSystem } from 'src/models/LandSystem';
import { SelectItem } from 'src/models/common/SelectItem';
import actions from 'src/actions';
import SurfaceExaminedHistoryDialog from '../../TractComponents/SurfaceExaminedHistoryDialog';
import TractDetailDocs from '../../LeasingComponents/TractDetailDocs';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import SurfaceTract from 'src/models/SurfaceTract';
import RHFEastTexasSurveyPanel from '../../TractComponents/panels/EastTexasSurvePanel.redesign';
import Abstract from 'src/models/Abstract';

interface ISurfaceTractCardRHFProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'control' | 'getValues' | 'setValue' | 'formState'>> {
    surfaceTract: SurfaceTract;
    landSystem: LandSystem;
    counties: County[];
    projectTags: Tag[];
    surfaceTags: Tag[];
    surfaceAbstracts: Abstract[];
    updateSurfaceTags: (tags: Tag[]) => void;
    updateSurfaceAbstracts: (abstracts: Abstract[]) => void;
    updateGrossAcres: () => void;
    updateSurfaceTract: (surfaceTract: SurfaceTract) => void;
    onChanges: () => void;
}

const SurfaceTractCardRHF: React.FC<ISurfaceTractCardRHFProps> = (props) => {
    const tagsRef = React.createRef<ReactTags>();
    const dispatch = useDispatch();
    const dialogContext = useContext<dialogCreators>(DialogContext);

    const [recordType, setRecordType] = useState<number>(0);
    
    useEffect(() => {
        updateRecordType();
    }, [props.register]);
    
    const updateRecordType = () => {
        let ref = props.getValues();
        let recordTypeId = parseInt(ref?.recordTypeId + '', 10);
        setRecordType(recordTypeId);
    };

    const addTag = (tag: Tag) => {
        if (props.surfaceTags?.length === 0) {
            props.updateSurfaceTags([tag]);
        } else if (props.surfaceTags?.filter((x: Tag) => x.name.trim().toLowerCase() === tag.name.trim().toLowerCase()).length === 0) {
            props.updateSurfaceTags([...props.surfaceTags, tag]);
        }
    };

    const deleteTag = (index: number) => {
        props.updateSurfaceTags(props.surfaceTags ? props.surfaceTags?.filter((x: Tag, i: number) => i !== index) : []);
    };

    const addAbstract = (abstract: Abstract) => {
        if (props.surfaceAbstracts?.length === 0) {
            props.updateSurfaceAbstracts([abstract]);
        } else if (props.surfaceAbstracts?.filter((x: Abstract) => x.name.trim().toLowerCase() === abstract.name.trim().toLowerCase()).length === 0) {
            props.updateSurfaceAbstracts([...props.surfaceAbstracts, abstract]);
        }
    };

    const deleteAbstract = (index: number) => {
        props.updateSurfaceAbstracts(props.surfaceAbstracts ? props.surfaceAbstracts?.filter((x: Abstract, i: number) => i !== index) : []);
    };

    const onLink = () => {
        let ref = props.getValues();
        let address = ref?.miscHyperlink;

        if (address) {
            const pattern = new RegExp('^(https?|ftp)://');

            if (!pattern.test(address)) {
                address = 'http://' + address;
            }

            window.open(address, '_blank');
        }
    };

    const showDialog = (dialogName: string) => {
        dispatch(actions.layout.showDialog(dialogName));
    };

    const onOpenTractNotes = () => {
        dialogContext.tract.openTractNotes(
            undefined,
            props.surfaceTract,
            '',
            null,
            props.updateSurfaceTract,
            null
        );
    };

    return (
        <>
            <Card newContext={true}>
                <div className="clearfix tract-data" style={{ padding: '1rem', boxSizing: 'border-box' }}>
                    <div className="entry-history" style={{ marginBottom: '1.5rem' }}>
                        <span style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '1rem', position: 'relative' }}>
                            <Button
                                intent="primary"
                                text="Surface Tract Notes"
                                onClick={() => onOpenTractNotes()}
                                size="medium"
                                disabled={props.formState.isDirty}
                            />
                        </span>
                    </div>
                    <FormRow>
                        <RHFInputBox label="Gross Acres" name="acres" type="number" register={props.register} errors={props.errors} onChange={props.updateGrossAcres} />
                        <RHFSelect 
                            label="County" 
                            name="countyId"
                            options={props.counties.map((c: County) => {
                                return {value: c.id, label: c.name};
                            })}
                            register={props.register}
                            errors={props.errors}
                        />
                        <RHFSelect 
                            label="Record Type" 
                            name="recordTypeId"
                            options={[{value: 1, label: 'Book/Page'}, {value: 2, label: 'Instrument'}]}
                            onChange={() => updateRecordType()}
                            register={props.register}
                            errors={props.errors}
                        />
                        <RHFInputBox name="recordDate" label="Deed Date" type="date" register={props.register} errors={props.errors} />
                        {recordType === 1 && (
                            <>
                                <RHFInputBox name="book" label="Book/Doc" register={props.register} errors={props.errors} />
                                <RHFInputBox name="page" label="Page" register={props.register} errors={props.errors} />
                            </>
                        )}
                        {recordType === 2 && (
                            <>
                                <RHFInputBox name="instrumentNumber" label="Instrument Number" register={props.register} errors={props.errors} />
                            </>
                        )}
                        <RHFSelect 
                            options={convertInstrumentTypesToSelectList().map((it: SelectItem) => {
                                return {value: it.id, label: it.name};
                            })}
                            label="Instrument Type" 
                            name="instrumentTypeId"
                            register={props.register}
                            errors={props.errors}
                        />
                    </FormRow>

                    <FormRow>
                        <RHFInputBox label="Surface Tract Name" name="name" register={props.register} errors={props.errors} style={{ width: '100%' }} bodyStyle={{ width: '20%' }} />
                        <RHFInputBox name="grantor" label="Grantor" register={props.register} errors={props.errors} style={{ width: '100%' }} bodyStyle={{ width: '40%' }} />
                        <RHFInputBox name="grantee" label="Grantee" register={props.register} errors={props.errors} style={{ width: '100%' }} bodyStyle={{ width: '40%' }} />
                    </FormRow>
                    {props.landSystem === LandSystem.Eastern && (
                        <>
                            <FormRow className="row" style={{ marginTop: 0 }}>
                                <RHFInputBox name="boundByNorth" label="Book By North" register={props.register} errors={props.errors} />
                                <RHFInputBox name="boundBySouth" label="Bound By South"register={props.register} errors={props.errors} />
                                <RHFInputBox name="boundByEast" label="Bound By East" register={props.register} errors={props.errors} />
                                <RHFInputBox name="boundByWest" label="Bound By West" register={props.register} errors={props.errors} />
                            </FormRow>
                        </>
                    )}
                    {props.landSystem !== LandSystem.Eastern && (
                        <>
                            <FormRow className="row">
                                <RHFTextArea 
                                    label="Legal Description"
                                    name="legalDescription"
                                    style={{ width: '100%' }}
                                    register={props.register}
                                    errors={props.errors}
                                />
                            </FormRow>
                        </>
                    )}
                    <div style={{ width: '100%', display: 'flex' }}>
                        <div style={{ width: '65%', display: 'flex', marginRight: '1rem' }}>
                            <div className="react-tags__label">Tags</div>
                            <br />
                            <ReactTags
                                ref={tagsRef}
                                tags={props.surfaceTags}
                                onAddition={addTag}
                                onDelete={deleteTag}
                                allowNew={true}
                                tagComponent={TagComponent}
                                suggestions={props.projectTags}
                                allowBackspace={false}
                            />
                        </div>
                        <div style={{ width: '30%', display: 'flex' }}>
                            <RHFInputBox name="miscHyperlink" label="Misc Hyperlink" register={props.register} errors={props.errors} style={{ width: '100%' }} />
                            <div style={{ position: 'relative', marginLeft: '1rem' }}>
                                <Button
                                    style={{ marginBottom: '1.75rem', marginTop: '1.75rem' }}
                                    text="Link"
                                    onClick={onLink}
                                />
                            </div>
                        </div>
                    </div>

                    {props.landSystem === LandSystem.EastTexas && (
                        <RHFEastTexasSurveyPanel 
                            counties={props.counties}
                            abstracts={props.surfaceAbstracts}
                            addAbstract={addAbstract}
                            deleteAbstract={deleteAbstract}
                            setValue={props.setValue}
                            getValues={props.getValues}
                        />
                    )}

                    {/* TODO: Resolve Surface Ownership Logic */}
                    <TractDetailDocs
                        showDialog={(dialogName: string) => showDialog(dialogName)}
                        hideDialog={() => dispatch(actions.layout.hideDialog())}
                        hasOwnership={false}
                        tractType="Surface"
                    />

                    <div>
                        <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0', marginTop: '1rem' }}>
                            <legend className="legend">Title Examination History</legend>
                        </fieldset>
                        {props.surfaceTract.examinedHistories?.length > 0 && (
                            <FormRow>
                                <label className="form-label mr-2">
                                    <b>Report Date:</b>
                                    <span className="readonly-field">{props.surfaceTract.examinedHistories[0]?.examinedDate}</span>
                                </label>
                                <label className="form-label mr-2">
                                    <b>Examined By:</b>
                                    <span className="readonly-field">{props.surfaceTract.examinedHistories[0]?.examinedByLandman}</span>
                                </label>

                                <label className="form-label mr-2">
                                    <b>Certification Date:</b>
                                    <span className="readonly-field">{props.surfaceTract.examinedHistories[0]?.takeOffDate}</span>
                                </label>

                                <label className="form-label mr-2">
                                    <b>Examination Type:</b>
                                    <span className="readonly-field">{props.surfaceTract.examinedHistories[0]?.ownershipType}</span>
                                </label>
                            </FormRow>
                        )}
                        {!props.surfaceTract.examinedHistories || props.surfaceTract.examinedHistories.length === 0 && (
                            <FormRow>
                                <label className="form-label mr-2">
                                    <b>No History Available</b>
                                </label>
                            </FormRow>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3rem 0 1.5rem 0' }}>
                            <Button
                                intent="primary"
                                onClick={() => showDialog(DialogList.SURFACE_EXAMINED_HISTORY)}
                                text="Manage Title Examination History"
                                size="medium"
                                style={{ marginRight: '1rem' }}
                                disabled={props.formState.isDirty}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <SurfaceExaminedHistoryDialog
                dialogName={DialogList.SURFACE_EXAMINED_HISTORY}
                currentExaminedHistory={props.surfaceTract.examinedHistories}
                hideDialog={() => dispatch(actions.layout.hideDialog())}
                getData={() => props.onChanges()}
            />
        </>
    );
};

export default SurfaceTractCardRHF;


// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/SurfaceTractCard.redesign.tsx